/**
 * NewHeroSection Component
 * Professional hero with logo, Arabic text, and product grid
 * Based on WordPress design reference
 */

import { Product } from "@/hooks/useProducts";
import ProductGridShowcase from "./ProductGridShowcase";
import { cn } from "@/lib/utils";

interface NewHeroSectionProps {
  products: Product[];
  className?: string;
}

const NewHeroSection = ({ products, className }: NewHeroSectionProps) => {
  return (
    <section
      className={cn(
        "relative overflow-hidden",
        "bg-gradient-to-br from-gray-900 via-gray-800 to-black",
        "py-12 md:py-16 lg:py-20",
        className
      )}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 35px,
              rgba(0, 255, 255, 0.1) 35px,
              rgba(0, 255, 255, 0.1) 70px
            )`
          }}
        />
      </div>

      {/* Animated CMYK Dots */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-32 h-32 bg-brand-cyan rounded-full blur-3xl opacity-20 animate-pulse" />
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-brand-magenta rounded-full blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-brand-yellow rounded-full blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Column - Logo & Arabic Text */}
          <div className="space-y-6 lg:space-y-8">
            {/* Logo Section */}
            <div className="flex items-center gap-4">
              {/* Logo */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-cyan via-brand-magenta to-brand-yellow rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
                <div className="relative bg-white rounded-2xl p-4 shadow-2xl transform group-hover:scale-105 transition-transform">
                  {/* CMYK Logo */}
                  <div className="grid grid-cols-2 gap-1 w-16 h-16 md:w-20 md:h-20">
                    <div className="bg-brand-cyan rounded-tl-lg" />
                    <div className="bg-brand-magenta rounded-tr-lg" />
                    <div className="bg-brand-yellow rounded-bl-lg" />
                    <div className="bg-brand-key rounded-br-lg" />
                  </div>
                </div>
              </div>

              {/* Brand Name */}
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold">
                  <span className="bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow bg-clip-text text-transparent">
                    AAAprinterz
                  </span>
                </h1>
                <p className="text-gray-400 text-sm md:text-base mt-1">
                  CMYK Printing Studio
                </p>
              </div>
            </div>

            {/* Arabic Creative Text */}
            <div className="space-y-4">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-brand-cyan/20 via-brand-magenta/20 to-brand-yellow/20 rounded-lg blur-xl" />
                <h2 className="relative text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                  <span className="block" style={{ fontFamily: 'Cairo, sans-serif' }}>
                    شـ_ــش
                  </span>
                </h2>
              </div>

              <p className="text-xl md:text-2xl lg:text-3xl text-white font-semibold">
                Printing for Everyone
              </p>

              <p className="text-lg md:text-xl text-brand-cyan font-medium italic">
                What's more, we do it right!
              </p>
            </div>

            {/* Stats */}
            <div className="flex gap-6 md:gap-8 pt-4">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-brand-cyan to-brand-magenta bg-clip-text text-transparent">
                  10K+
                </div>
                <div className="text-xs md:text-sm text-gray-400">Happy Customers</div>
              </div>
              <div className="w-px bg-gray-700" />
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-brand-magenta to-brand-yellow bg-clip-text text-transparent">
                  500+
                </div>
                <div className="text-xs md:text-sm text-gray-400">Products</div>
              </div>
              <div className="w-px bg-gray-700" />
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-brand-yellow to-brand-cyan bg-clip-text text-transparent">
                  24/7
                </div>
                <div className="text-xs md:text-sm text-gray-400">Support</div>
              </div>
            </div>
          </div>

          {/* Right Column - Product Grid */}
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute -inset-4 bg-gradient-to-br from-brand-cyan/20 via-brand-magenta/20 to-brand-yellow/20 rounded-3xl blur-2xl" />

            {/* Grid Container */}
            <div className="relative">
              <ProductGridShowcase products={products} />
            </div>

            {/* Floating Badge */}
            <div className="absolute -bottom-4 -left-4 bg-gradient-to-br from-gray-900 to-gray-800 border border-brand-cyan/50 rounded-2xl p-4 shadow-2xl backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-brand-cyan to-brand-magenta flex items-center justify-center text-2xl">
                  🎨
                </div>
                <div>
                  <div className="font-bold text-white">New Arrivals</div>
                  <div className="text-sm text-brand-cyan">Fresh Designs Daily</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewHeroSection;